import { useEffect, useRef, useState, useMemo } from "react";
import useDarkMode from "../../hooks/useDarkMode";
import useThemeColor from "../../hooks/useThemeColor";
import showErrorToast, { showSuccessToast } from "../../components/CustomComponents/toast/toast";
import axiosInstance from "../../config/axiosApi/axiosApi";
import APIs from "../../Constants/apis";
import CustomModal from "../../components/CustomComponents/modal/customModal";
import FormInput from "../../components/CustomComponents/Input/formInput";
import {
  estimateSmsSegments,
  insertTokenAtCursor,
  resolveMessageWithSamples,
} from "../../utils/smsTemplateUtils";
import { filterSmsContentPlaceholders } from "../../utils/marketingTemplateUtils";

const emptyForm = {
  presetTitle: "",
  message: "",
};

function SmsPresetModal({
  isModalOpen,
  setIsModalOpen,
  onPresetSaved,
  compID,
  presetToEdit = null,
}) {
  const [form, setForm] = useState(emptyForm);
  const [saving, setSaving] = useState(false);
  const [placeholders, setPlaceholders] = useState([]);
  const [marketingSettings, setMarketingSettings] = useState({
    reservationUrl: "",
    menuUrl: "",
  });
  const [previewText, setPreviewText] = useState("");
  const [previewLoading, setPreviewLoading] = useState(false);
  const messageRef = useRef(null);
  const isDarkMode = useDarkMode();
  const themeColor = useThemeColor();

  useEffect(() => {
    if (!isModalOpen || !compID) return;

    const loadPlaceholders = async () => {
      try {
        const response = await axiosInstance.get(APIs.GET_SMS_PRESET_PLACEHOLDERS, {
          params: { CompID: compID },
        });
        const rows = response.data?.data ?? response.data?.Data ?? [];
        setPlaceholders(filterSmsContentPlaceholders(Array.isArray(rows) ? rows : []));
      } catch {
        setPlaceholders([]);
      }
    };

    const loadMarketingSettings = async () => {
      try {
        const response = await axiosInstance.get(APIs.GET_MARKETING_SETTINGS, {
          params: { CompID: compID },
        });
        const data = response.data?.data ?? response.data?.Data ?? {};
        setMarketingSettings({
          reservationUrl: data.reservationUrl ?? data.ReservationUrl ?? "",
          menuUrl: data.menuUrl ?? data.MenuUrl ?? "",
        });
      } catch {
        setMarketingSettings({ reservationUrl: "", menuUrl: "" });
      }
    };

    loadPlaceholders();
    loadMarketingSettings();
  }, [isModalOpen, compID]);

  useEffect(() => {
    if (presetToEdit) {
      setForm({
        presetTitle: presetToEdit.presetTitle ?? presetToEdit.PresetTitle ?? "",
        message: presetToEdit.message ?? presetToEdit.Message ?? "",
      });
    } else {
      setForm(emptyForm);
    }
  }, [presetToEdit, isModalOpen]);

  const smsStats = useMemo(
    () => estimateSmsSegments(form.message),
    [form.message]
  );

  const previewPlaceholders = useMemo(() => {
    const reservationSample =
      marketingSettings.reservationUrl?.trim() ||
      "https://example.com/restaurant/reservation";
    const menuSample =
      marketingSettings.menuUrl?.trim() || "https://example.com/restaurant";

    return placeholders.map((p) => {
      const key = p.key ?? p.Key;
      if (key === "ReservationUrl") {
        return { ...p, sampleValue: reservationSample, SampleValue: reservationSample };
      }
      if (key === "MenuUrl") {
        return { ...p, sampleValue: menuSample, SampleValue: menuSample };
      }
      return p;
    });
  }, [placeholders, marketingSettings]);

  const localPreview = useMemo(
    () => resolveMessageWithSamples(form.message, previewPlaceholders),
    [form.message, previewPlaceholders]
  );

  useEffect(() => {
    setPreviewText(localPreview);
  }, [localPreview]);

  const handleCloseModal = () => {
    setIsModalOpen(false);
    setForm(emptyForm);
    setPreviewText("");
  };

  const handleInsertPlaceholder = (token) => {
    setForm((prev) => ({
      ...prev,
      message: insertTokenAtCursor(messageRef.current, prev.message, token),
    }));
  };

  const refreshServerPreview = async () => {
    if (!form.message.trim()) {
      setPreviewText("");
      return;
    }
    try {
      setPreviewLoading(true);
      const response = await axiosInstance.post(APIs.POST_PREVIEW_SMS_PRESET_MESSAGE, {
        message: form.message,
        compID: Number(compID),
      });
      const resolved =
        response.data?.data?.resolvedMessage ??
        response.data?.Data?.resolvedMessage ??
        localPreview;
      setPreviewText(resolved);
    } catch {
      setPreviewText(localPreview);
    } finally {
      setPreviewLoading(false);
    }
  };

  const handleSubmit = async () => {
    const title = form.presetTitle?.trim();
    const message = form.message?.trim();

    if (!title) {
      showErrorToast("Please provide a preset title.");
      return;
    }
    if (!message) {
      showErrorToast("Please provide a message.");
      return;
    }
    if (!compID || Number(compID) <= 0) {
      showErrorToast("Company context is missing. Please sign in again.");
      return;
    }

    const existingGuid =
      presetToEdit?.smsPreset_Guid ?? presetToEdit?.SmsPreset_Guid ?? "";
    const isUpdate = Boolean(existingGuid);

    let smsPresetGuid = existingGuid;
    if (!smsPresetGuid) {
      if (typeof crypto !== "undefined" && crypto.randomUUID) {
        smsPresetGuid = crypto.randomUUID();
      } else {
        showErrorToast("Could not generate a preset identifier. Please try again.");
        return;
      }
    }

    const payload = {
      smsPreset_Guid: smsPresetGuid,
      compID: Number(compID),
      presetTitle: title,
      message,
      isActive: presetToEdit?.isActive ?? presetToEdit?.IsActive ?? true,
    };

    try {
      setSaving(true);
      const response = await axiosInstance.post(APIs.POST_ADD_UPDATE_SMS_PRESET, payload);

      if (!response.data?.status) {
        showErrorToast(response.data?.message || "Could not save SMS preset.");
        return;
      }

      const saved = response.data?.data ?? response.data?.Data ?? null;
      showSuccessToast(
        isUpdate ? "SMS preset updated successfully." : "SMS preset created successfully."
      );
      onPresetSaved?.(saved, isUpdate);
      handleCloseModal();
    } catch (err) {
      showErrorToast(
        err.response?.data?.message || "Something went wrong while saving the SMS preset."
      );
    } finally {
      setSaving(false);
    }
  };

  const textareaBorder = isDarkMode ? "#6b7280" : "#d1d5db";
  const panelBg = isDarkMode ? "#1e1e1e" : "#f8f9fa";
  const panelBorder = isDarkMode ? "#404040" : "#e9ecef";

  return (
    <CustomModal
      isOpen={isModalOpen}
      onClose={handleCloseModal}
      onSubmit={handleSubmit}
      title={presetToEdit ? "Update SMS Preset" : "Create New SMS Preset"}
      showButton={true}
      isCentered={true}
      dialogMaxWidth="640px"
      submitButtonText={presetToEdit ? "Save" : "Save"}
      submitButtonDisabled={saving}
      submitButtonStyle={{
        width: "auto",
        minWidth: "120px",
        whiteSpace: "nowrap",
        paddingLeft: "24px",
        paddingRight: "24px",
      }}
      minHeight="auto"
      maxHeight="90vh"
    >
      <div className="mb-3">
        <FormInput
          label="Preset Title"
          placeHolder="Enter preset title"
          type="text"
          value={form.presetTitle}
          onchange={(e) =>
            setForm((prev) => ({ ...prev, presetTitle: e.target.value }))
          }
        />
      </div>

      <div className="mb-2">
        <label
          className="form-label"
          style={{ color: isDarkMode ? "#ffffff" : "#6c757d" }}
        >
          Message
        </label>
        <textarea
          ref={messageRef}
          className="form-control"
          rows={5}
          placeholder="Type your SMS preset message"
          value={form.message}
          onChange={(e) =>
            setForm((prev) => ({ ...prev, message: e.target.value }))
          }
          style={{
            borderRadius: "10px",
            border: `1px solid ${textareaBorder}`,
            minHeight: "120px",
            resize: "vertical",
            background: isDarkMode ? "#2d3436" : "#ffffff",
            color: isDarkMode ? "#ffffff" : undefined,
          }}
          onFocus={(e) => {
            e.target.style.borderColor = themeColor;
            e.target.style.boxShadow = `0 0 0 2px ${themeColor}33`;
          }}
          onBlur={(e) => {
            e.target.style.borderColor = textareaBorder;
            e.target.style.boxShadow = "none";
          }}
        />
        <div className="d-flex flex-wrap justify-content-between align-items-center mt-1 gap-2">
          <small className="text-muted">
            {smsStats.length} characters · {smsStats.segments} segment
            {smsStats.segments === 1 ? "" : "s"}
            {!smsStats.isGsm7 && " · Unicode may reduce length per segment"}
          </small>
        </div>
      </div>

      <div className="mb-3">
        <label
          className="form-label mb-2"
          style={{ color: isDarkMode ? "#ffffff" : "#6c757d", fontSize: "13px" }}
        >
          Insert dynamic field
        </label>
        <div className="d-flex flex-wrap gap-2">
          {placeholders.map((p) => {
            const token = p.token ?? p.Token;
            const label = p.label ?? p.Label ?? token;
            return (
              <button
                key={p.key ?? p.Key ?? token}
                type="button"
                className="btn btn-sm"
                onClick={() => handleInsertPlaceholder(token)}
                style={{
                  background: isDarkMode ? "#404040" : "#ffffff",
                  border: `1px solid ${isDarkMode ? "#555" : "#dee2e6"}`,
                  color: isDarkMode ? "#ffffff" : "#495057",
                  borderRadius: "20px",
                  fontSize: "12px",
                }}
              >
                {label}
              </button>
            );
          })}
        </div>
      </div>

      <div
        className="rounded-3 p-3 mb-2"
        style={{
          background: panelBg,
          border: `1px solid ${panelBorder}`,
        }}
      >
        <div className="d-flex justify-content-between align-items-center mb-2">
          <span
            style={{
              fontSize: "13px",
              fontWeight: 600,
              color: isDarkMode ? "#ffffff" : "#495057",
            }}
          >
            Preview
          </span>
          <button
            type="button"
            className="btn btn-link btn-sm p-0"
            onClick={refreshServerPreview}
            disabled={previewLoading}
            style={{ color: themeColor, textDecoration: "none" }}
          >
            {previewLoading ? "Refreshing…" : "Refresh preview"}
          </button>
        </div>
        <p
          className="mb-0"
          style={{
            fontSize: "14px",
            whiteSpace: "pre-wrap",
            color: isDarkMode ? "#e9ecef" : "#212529",
          }}
        >
          {previewText || "Preview will appear here."}
        </p>
      </div>
    </CustomModal>
  );
}

export default SmsPresetModal;
